import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { useSession } from '@/features/auth/api/useSession'
import { useSetClientOwner } from '@/features/clients/api/useSetClientOwner'
import { ClientOwnerSheet } from '@/features/clients/components/ClientOwnerSheet'
import {
  useClients,
  useDeleteClient,
  useMergeClients,
  useSetClientActive,
  useUpdateClient,
} from '@/features/directories/api/useDirectory'
import { ClientFormSheet, type ClientDraft } from '@/features/directories/components/DirectoryFormSheets'
import { MergeSheet } from '@/features/directories/components/MergeSheet'
import {
  clientMergeErrorMessage,
  clientSaveErrorMessage,
  referencedMessage,
  referencedRefusal,
} from '@/features/directories/directoryText'
import { CAP, can } from '@/lib/can'
import { describeProblem } from '@/lib/problems'
import type { Client } from '@/types/domain'

/* The record's acts — edit, change the sales contact, merge, (de)activate,
 * delete — with the refused delete that NAMES the referencing quotes and cases
 * (FR-39) and offers the merge that resolves it (Story 7.1 AC 4). Moved here
 * from the directory tab unchanged in behaviour: the client's own page is where
 * a reader is when they decide to act on it.
 *
 * Affordance only. The server refuses regardless (FR-44); hiding a button
 * that would always answer 403 is kinder than offering it. */
export function ClientRecordActions({
  client,
  ownerName,
}: {
  client: Client
  /** The present sales contact's name, for the reassignment sheet. */
  ownerName?: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: session } = useSession()
  /* Merge, (de)activate, delete and the reassignment are one directory admin's
     acts, gated on the CAPABILITY (`directory.client.manage`, the client-list
     grant) — exactly what the server checks on each of them. Never on the role
     string: a founder-made role holding the capability matches no `super-admin`
     arm and was offered none of them (see lib/can.ts). The superuser
     short-circuits inside `can`. */
  const canManage = can(session, CAP.directoryClientManage)

  const [editing, setEditing] = useState(false)
  const [merging, setMerging] = useState(false)
  const [reassigning, setReassigning] = useState(false)
  const setOwner = useSetClientOwner(client.id)
  const update = useUpdateClient()
  const remove = useDeleteClient()
  const setActive = useSetClientActive()
  const merge = useMergeClients()
  // Candidates for a merge: every client the caller may see. Fetched only
  // once the sheet is asked for.
  const { data: candidates } = useClients(undefined, { enabled: merging })

  const refusal = referencedRefusal(remove.error)

  /* The opener owns each sheet's mutation, so the opener clears it: the last
     attempt's refusal belongs to the last attempt, not to a sheet the reader
     has not submitted yet (frontend/README). */
  const openEdit = () => {
    update.reset()
    setEditing(true)
  }
  const openMerge = () => {
    merge.reset()
    setMerging(true)
  }

  const submit = (values: ClientDraft) => {
    update.mutate({ id: client.id, payload: { ...values } }, { onSuccess: () => setEditing(false) })
  }

  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="secondary" onClick={openEdit}>
          {t('clients.actions.edit')}
        </Button>
        {canManage ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setOwner.reset()
                setReassigning(true)
              }}
            >
              {t('clients.owner.action')}
            </Button>
            <Button size="sm" variant="ghost" onClick={openMerge}>
              {t('clients.actions.merge')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={setActive.isPending}
              onClick={() => setActive.mutate({ id: client.id, isActive: !client.isActive })}
            >
              {client.isActive ? t('directories.deactivate') : t('directories.activate')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(client.id, { onSuccess: () => navigate('/clients', { replace: true }) })
              }
            >
              {t('directories.clients.delete')}
            </Button>
          </>
        ) : null}
      </div>

      {refusal ? (
        <Banner
          tone="attention"
          title={t('directories.clients.delete')}
          action={
            refusal.suggestion === 'merge' ? (
              <Button size="sm" variant="secondary" onClick={() => { openMerge(); remove.reset() }}>
                {t('directories.merge.action')}
              </Button>
            ) : undefined
          }
        >
          {referencedMessage(refusal, t)}
        </Banner>
      ) : null}

      {/* Only the (de)activation's refusal is said here: it has no sheet. The
          edit and the merge are refused INSIDE the sheet that sent them, and
          their openers reset them — repeating the refusal as a block on the
          page behind the sheet said it twice, and left it standing after the
          reader had cancelled.

          A Banner, like the refused delete above, and not an ErrorState: its
          «Повторить» only cleared the error — a promise of a retry that sent
          nothing, under a refusal that would answer the same again. The one
          act offered is the one it performs, hiding it. In a live region,
          because it appears on a press with nothing else moving. */}
      {setActive.error ? (
        <div role="alert">
          <Banner
            tone="attention"
            title={t(describeProblem(setActive.error).titleKey)}
            action={
              <Button size="sm" variant="ghost" onClick={() => setActive.reset()}>
                {t('common.actions.dismiss')}
              </Button>
            }
          >
            {t(describeProblem(setActive.error).detailKey)}
          </Banner>
        </div>
      ) : null}

      <ClientFormSheet
        open={editing}
        onClose={() => setEditing(false)}
        record={client}
        pending={update.isPending}
        error={update.error ? clientSaveErrorMessage(update.error, t) : undefined}
        onSubmit={submit}
      />

      {canManage ? (
        <ClientOwnerSheet
          open={reassigning}
          onClose={() => setReassigning(false)}
          client={client}
          currentOwnerName={ownerName}
          pending={setOwner.isPending}
          error={setOwner.error}
          onConfirm={(ownerId) =>
            setOwner.mutate(ownerId, { onSuccess: () => setReassigning(false) })
          }
        />
      ) : null}

      <MergeSheet
        open={merging}
        onClose={() => setMerging(false)}
        source={merging ? client : undefined}
        candidates={candidates ?? []}
        pending={merge.isPending}
        /* The merge's own sentence: a client a debit note bills is refused
           naming the note (S-22d2), never with the delete's wording. */
        error={merge.error ? clientMergeErrorMessage(merge.error, t) : undefined}
        onConfirm={(targetId) =>
          merge.mutate(
            { targetId, sourceId: client.id },
            // The source is gone after a merge; the kept record is where the
            // reader continues.
            { onSuccess: () => { setMerging(false); navigate(`/clients/${targetId}`, { replace: true }) } },
          )
        }
      />
    </>
  )
}
